/**
 * API Client with retry logic and error handling
 * Implements Requirements 3.3, 3.4, 3.7
 */

import { config } from '../config/env';
import { TranslationRequest, TranslationResponse, TTSRequest, TTSVoice } from '../types';
import { cacheService } from './cacheService';
import { mapFrontendMode, FrontendMode } from '../config/translationModes';

interface ApiError {
  code: string;
  message: string;
  details?: unknown;
}

interface RetryConfig {
  maxRetries: number;
  initialDelay: number;
  maxDelay: number;
  backoffMultiplier: number;
}

const DEFAULT_RETRY_CONFIG: RetryConfig = {
  maxRetries: 3,
  initialDelay: 1000, // 1 second
  maxDelay: 10000, // 10 seconds
  backoffMultiplier: 2,
};

/**
 * Sleep utility for retry delays
 */
function sleep(ms: number): Promise<void> {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

/**
 * Calculate exponential backoff delay
 */
function calculateBackoff(attempt: number, config: RetryConfig): number {
  const delay = config.initialDelay * Math.pow(config.backoffMultiplier, attempt);
  return Math.min(delay, config.maxDelay);
}

/**
 * Determine if an error is retryable
 */
function isRetryableError(error: unknown): boolean {
  if (error instanceof TypeError && error.message.includes('fetch')) {
    // Network errors are retryable
    return true;
  }

  if (error && typeof error === 'object' && 'status' in error) {
    const status = (error as { status: number }).status;
    // Retry on 5xx server errors and 429 rate limit
    return status >= 500 || status === 429;
  }

  return false;
}

/**
 * HTTP Client with retry logic
 */
class ApiClient {
  private baseUrl: string;
  private timeout: number;
  private retryConfig: RetryConfig;

  constructor() {
    this.baseUrl = config.api.url;
    this.timeout = config.api.timeout;
    this.retryConfig = DEFAULT_RETRY_CONFIG;
  }

  /**
   * Make a request with retry logic
   */
  private async requestWithRetry<T>(
    url: string,
    options: RequestInit,
    attempt = 0
  ): Promise<T> {
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), this.timeout);

    try {
      const response = await fetch(url, {
        ...options,
        signal: controller.signal,
      });

      clearTimeout(timeoutId);

      // Handle non-OK responses
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({
          code: 'HTTP_ERROR',
          message: `HTTP ${response.status}: ${response.statusText}`,
        }));

        const error = new Error(errorData.message || 'Request failed') as Error & {
          status: number;
          code: string;
          details?: unknown;
        };
        error.status = response.status;
        error.code = errorData.code || 'HTTP_ERROR';
        error.details = errorData.details;

        throw error;
      }

      // Parse successful response
      const data = await response.json();
      return data as T;
    } catch (error) {
      clearTimeout(timeoutId);

      // Check if we should retry
      if (attempt < this.retryConfig.maxRetries && isRetryableError(error)) {
        const delay = calculateBackoff(attempt, this.retryConfig);
        console.warn(
          `Request failed (attempt ${attempt + 1}/${this.retryConfig.maxRetries}), retrying in ${delay}ms...`,
          error
        );
        await sleep(delay);
        return this.requestWithRetry<T>(url, options, attempt + 1);
      }

      // No more retries or non-retryable error
      throw error;
    }
  }

  /**
   * Translate text using the backend API
   * Implements caching with 24-hour TTL (Requirements 13.1, 13.2)
   */
  async translate(request: TranslationRequest): Promise<TranslationResponse> {
    // Check cache first
    const cachedResponse = cacheService.get(request);
    if (cachedResponse) {
      console.log('Translation served from cache');
      return cachedResponse;
    }

    const url = `${this.baseUrl}/api/v1/translate`;

    // Map frontend types to backend API format
    const backendMode = mapFrontendMode((request.mode || 'translate') as FrontendMode);
    const backendRequest = {
      text: request.source,
      sourceLanguage: request.sourceLang,
      targetLanguage: request.targetLang,
      mode: backendMode,
      context: {
        gender: request.genderPreferences.targetGender || 'neutral',
        register: this.mapContextToRegister(request.context),
        tone: this.mapContextToTone(request.context),
      },
    };

    try {
      const response = await this.requestWithRetry<{
        success: boolean;
        data?: {
          translatedText: string;
          variants: Array<{ text: string; confidence: number }>;
          sourceText: string;
          sourceLanguage: string;
          targetLanguage: string;
          mode: string;
          context?: {
            gender?: string;
            register?: string;
            tone?: string;
            region?: string;
          };
          metadata: {
            processingTime: number;
            passes: {
              pass1: { status: string; processingTime: number };
              pass2: { status: string; processingTime: number };
              pass3: { status: string; processingTime: number };
            };
            confidence: number;
            timestamp: string;
          };
        };
        error?: ApiError;
      }>(url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(backendRequest),
      });

      // Handle backend error response
      if (!response.success || !response.data) {
        throw new Error(response.error?.message || 'Translation failed');
      }

      // Map backend response to frontend format
      const translationResponse: TranslationResponse = {
        translation: response.data.translatedText,
        qualityScore: response.data.metadata.confidence,
        confidence: response.data.metadata.confidence,
        modelChain: ['Phi3', 'Llama3.2', 'Llama3.2'], // 3-pass pipeline
        latency: response.data.metadata.processingTime,
        alternatives: response.data.variants
          .filter((v) => v.text !== response.data!.translatedText)
          .map((v) => v.text)
          .slice(0, 2), // Show top 2 alternatives
        metadata: {
          timestamp: new Date(response.data.metadata.timestamp),
          cached: false,
          templateUsed: request.templateTag,
        },
      };

      // Cache the successful response
      cacheService.set(request, translationResponse);

      return translationResponse;
    } catch (error) {
      // Transform error into user-friendly format
      if (error instanceof Error) {
        const apiError = error as Error & { status?: number; code?: string };
        
        // Provide specific error messages based on error type
        if (apiError.message.includes('fetch') || apiError.message.includes('network')) {
          throw new Error(
            'Network error: Unable to reach translation service. Please check your connection and try again.'
          );
        }

        if (apiError.status === 429) {
          throw new Error(
            'Rate limit exceeded. Please wait a moment and try again.'
          );
        }

        if (apiError.status && apiError.status >= 500) {
          throw new Error(
            'Translation service is temporarily unavailable. Please try again in a few moments.'
          );
        }

        if (apiError.status === 400) {
          throw new Error(
            apiError.message || 'Invalid translation request. Please check your input and try again.'
          );
        }

        // Generic error
        throw new Error(
          apiError.message || 'Translation failed. Please try again.'
        );
      }

      throw new Error('An unexpected error occurred. Please try again.');
    }
  }

  /**
   * Map frontend context to backend register
   */
  private mapContextToRegister(
    context: string
  ): 'formal' | 'informal' | 'academic' | 'business' {
    switch (context) {
      case 'formal':
        return 'formal';
      case 'casual':
        return 'informal';
      case 'business':
        return 'business';
      case 'academic':
        return 'academic';
      case 'creative':
        return 'informal'; // Map creative to informal
      default:
        return 'informal';
    }
  }

  /**
   * Map frontend context to backend tone
   */
  private mapContextToTone(
    context: string
  ): 'professional' | 'casual' | 'friendly' | 'authoritative' {
    switch (context) {
      case 'formal':
        return 'professional';
      case 'casual':
        return 'casual';
      case 'business':
        return 'professional';
      case 'academic':
        return 'authoritative';
      case 'creative':
        return 'friendly';
      default:
        return 'casual';
    }
  }

  /**
   * Generate TTS audio for the given text.
   * Calls POST /api/v1/tts and returns the audio as a Blob.
   */
  async speak(request: TTSRequest): Promise<Blob> {
    const url = `${this.baseUrl}/api/v1/tts`;
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), this.timeout);

    try {
      const response = await fetch(url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(request),
        signal: controller.signal,
      });

      clearTimeout(timeoutId);

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({
          message: `TTS failed: HTTP ${response.status}`,
        }));
        throw new Error(errorData.message || `TTS failed: HTTP ${response.status}`);
      }

      return await response.blob();
    } catch (error) {
      clearTimeout(timeoutId);
      if (error instanceof Error) {
        throw error;
      }
      throw new Error('TTS request failed unexpectedly.');
    }
  }

  /**
   * Fetch available TTS voices.
   * Calls GET /api/v1/tts/voices.
   */
  async getTTSVoices(): Promise<TTSVoice[]> {
    const url = `${this.baseUrl}/api/v1/tts/voices`;
    return this.requestWithRetry<TTSVoice[]>(url, { method: 'GET' });
  }

  /**
   * Health check endpoint
   */
  async healthCheck(): Promise<{ status: string; timestamp: string }> {
    const url = `${this.baseUrl}/api/v1/translate/health`;
    return this.requestWithRetry(url, { method: 'GET' });
  }
}

// Export singleton instance
export const apiClient = new ApiClient();
